import "./youtubePlayer.scss";
import React from 'react';
import { connect } from 'react-redux';
import { IState, actions } from '../../Store/reducer';
import createPlayer from 'youtube-player';
import type { YouTubePlayer } from 'youtube-player/dist/types';

interface IYouTubePlayerProps {
    setIsPlaying: (isPlaying: boolean) => void;
    isPlaying: boolean;
    setPlaylistId: (playlistId: string) => void;
    playlistId: string;
    sceneNumber: number;
    display: boolean;
}

interface IYouTubePlayerState {
    displayVolume: boolean;
    volume: number;
    currentTime: number;
    duration: number;
}

class YouTubePlaylist extends React.Component<IYouTubePlayerProps, IYouTubePlayerState> {

    player: YouTubePlayer | undefined;
    timer: NodeJS.Timer | null = null;

    constructor(props: IYouTubePlayerProps) {
        super(props);
        this.state = {
            displayVolume: false,
            volume: 50,
            currentTime: 0,
            duration: 0
        };
    }

    async onTick() {
        if (this.player && this.props.isPlaying) {
            this.setState({ currentTime: await this.player.getCurrentTime(), duration: await this.player.getDuration() });
        }
    }

    onPlayerStateChange = (event: any) => {
        if (event.data === 0) {
            this.loadPlaylist();
        }
    };

    async loadPlaylist() {
        await this.player!.stopVideo();
        await this.player!.loadPlaylist({
            listType: 'playlist',
            list: this.props.playlistId,
            index: 0,
            startSeconds: 0
        });
        await this.player!.setVolume(this.state.volume)
        this.props.setIsPlaying(true);
    }

    onPlayerReady = () => { }

    componentDidMount() {
        this.player = createPlayer("player", {
            width: "375px",
            height: "210px",
            playerVars: {
                autoplay: 1,
                loop: 1
            }
        });

        this.player.on('ready', this.onPlayerReady);
        this.player.on('stateChange', this.onPlayerStateChange);
        this.timer = setInterval(this.onTick.bind(this), 250);
    }

    async componentDidUpdate(prevProps: Readonly<IYouTubePlayerProps>, prevState: Readonly<IYouTubePlayerState>) {

        if (prevProps.playlistId !== this.props.playlistId) {
            await this.loadPlaylist();
        }

        if (await this.player!.getPlayerState() === 2 && this.props.isPlaying) { // 2 = pause
            await this.player!.playVideo();
        }

        if (await this.player!.getPlayerState() === 1 && !this.props.isPlaying) { // 1 = play
            await this.player!.pauseVideo();
        }

    }

    componentWillUnmount() {
        if (this.timer !== null) {
            clearInterval(this.timer);
        }
    }

    toggleVolume = () => {
        this.setState({ displayVolume: !this.state.displayVolume });
    }

    async onVolumeChange(event: { target: { value: string; }; }) {
        this.setState({ volume: Number(event.target.value) });
        await this.player!.setVolume(Number(event.target.value));
    }

    onPlayPauseClick = () => {
        this.props.setIsPlaying(!this.props.isPlaying)
    }

    async previousVideo() {
        await this.player!.previousVideo();
        this.props.setIsPlaying(true);
    }

    async nextVideo() {
        if ((await this.player!.getPlaylist()).length === await this.player!.getPlaylistIndex() + 1) {
            this.loadPlaylist();
        }
        await this.player!.nextVideo();
        this.props.setIsPlaying(true);
    }

    render() {
        return (
            <div className="playlist">

                <div id="player" />

                {this.props.playlistId && this.player && this.props.display &&
                    <div className="playlist-controls">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1073.27 205.37"><g><path fill="#fff" opacity=".8" stroke="#eb6224" strokeMiterlimit="10" strokeWidth="2px" d="m1.04,3.08l.76,32.23v54.49l20.96-6.68-18.08,12.59c0,1.69-1.12,13.6-.26,14.91l1.04,35.99-1.26,4.39,2.19,52.74,5.46.61,406.54-3.77.03,1.14v.57l652.98-14.28.84-12.78c0-1.72-.6-3.25-1.42-4.63l-1.31-55.86-12.17,3.91-38.81,5.03,45.01-14.73-.22-39.35h-1.13V6.75c0-1.72-.6-3.25-1.42-4.63l-.03-1.1H407.61l.05,2.07H1.04Z" /></g></svg>
                        <div className="playlist-controls-content">
                            <div className="playlist-input">
                                <div className="volume">
                                    <svg xmlns="http://www.w3.org/2000/svg" onClick={this.toggleVolume} viewBox="0 0 88.28 79.16"><g><path fill="none" stroke="#ec6729" strokeMiterlimit="10" strokeWidth="2.57px" d="m64.23,9.98h0c8.05,8.05,12.08,18.59,12.08,29.15s-4.03,21.1-12.08,29.15h0" /><path fill="none" stroke="#ec6729" strokeMiterlimit="10" strokeWidth="2.57px" d="m71.16.91h0c10.56,10.56,15.83,24.38,15.83,38.2s-5.27,27.66-15.83,38.2h0" /><path fill="none" stroke="#ec6729" strokeMiterlimit="10" strokeWidth="2.57px" d="m57.46,19.8h0c5.34,5.34,8,12.34,8,19.32s-2.66,13.98-8,19.32h0" /><path fill="none" stroke="#ec6729" strokeMiterlimit="10" strokeWidth="2.57px" d="m22.85,58.57l18.44,17.59V5.52l-18.37,17.52c-.26.28-.62.45-1.02.45H1.29v34.66h20.61c.35,0,.69.12.95.38v.03Z" /></g></svg>
                                    <div className="volume-progress-box" style={{ display: this.state.displayVolume ? "block" : "none" }}>
                                        <input className="volume-progress" type="range" min="0" max="100" value={this.state.volume} onChange={async (event) => await this.onVolumeChange(event)} />
                                    </div>
                                </div>
                                <div className="prev" onClick={() => this.previousVideo()}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72.9 71.59"><g><polygon fill="#fff" stroke="#eb6224" strokeMiterlimit="10" strokeWidth="2px" points="13.47 35.8 71.61 69.37 71.61 2.23 13.47 35.8" /><line fill="#fff" stroke="#eb6224" strokeMiterlimit="10" strokeWidth="2px" x1="1.29" y1="2.23" x2="1.29" y2="69.37" /></g></svg>
                                </div>
                                <div className="play-pause" onClick={this.onPlayPauseClick}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 124.15 71.59"><g><polygon fill={this.props.isPlaying ? "#eb6224" : "#fff"} stroke="#eb6224" strokeMiterlimit="10" strokeWidth={this.props.isPlaying ? "2px" : "4px"} points="121.58 35.8 63.44 2.23 63.44 69.37 121.58 35.8" /><line stroke="#eb6224" strokeWidth="2px" x1="1.29" y1="2.23" x2="1.29" y2="69.37" /><line stroke="#eb6224" strokeWidth={this.props.isPlaying ? "2px" : "4px"} x1="23.09" y1="2.23" x2="23.09" y2="69.37" /></g></svg>
                                </div>
                                <div className="next" onClick={() => this.nextVideo()}>
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 72.9 71.59"><g><polygon fill="#fff" stroke="#eb6224" strokeMiterlimit="10" strokeWidth="2px" points="59.43 35.8 1.29 2.23 1.29 69.37 59.43 35.8" /><line fill="#fff" stroke="#eb6224" strokeMiterlimit="10" strokeWidth="2px" x1="71.61" y1="69.37" x2="71.61" y2="2.23" /></g></svg>
                                </div>
                            </div>
                            <input className="time-progress" type="range" min="0" max={this.state.duration} value={this.state.currentTime | 0} readOnly />
                        </div>
                    </div>
                }
            </div>
        );
    }
}

const mapStateToProps = (state: IState) => ({
    playlistId: state.master.playlistId,
    isPlaying: state.master.isPlaying,
    sceneNumber: state.master.sceneNumber,
});

const mapDispatchToProps = (dispatch: any) => ({
    setIsPlaying: (isPlaying: boolean) => {
        dispatch({
            type: actions.master.SET_IS_PLAYING,
            payload: {
                master: { isPlaying }
            }
        })
    },
    setPlaylistId: (playlistId: string) => {
        dispatch({
            type: actions.master.SET_PLAYLIST_ID,
            payload: {
                master: { playlistId: playlistId }
            }
        })
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(YouTubePlaylist);